import React from 'react';
import I18n from 'i18n-js';
import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import Tiles from '@reverbdotcom/commons/src/components/tiles';
import { RCIcon } from '@reverbdotcom/cadence/components/RCIcon/RCIcon';
import { SearchIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { experimentInt } from '@reverbdotcom/commons/src/user_context_helpers';
import classNames from 'classnames';
import { reverb_personalization_SearchQuery } from '../../schemas/rql';
import withQuerySuggestions from '../with_query_suggestions';

interface IProps {
  querySuggestions?: reverb_personalization_SearchQuery[];
  loading?: boolean;
}

const COMPONENT_NAME = 'MarketplaceQuerySuggestions';

function backgroundImageStyle(suggestion) {
  let imageUrl;
  if (suggestion.listings) {
    imageUrl = suggestion.listings[0].images[0].source;
  }
  return { backgroundImage: `url(${imageUrl})` };
}

function buildClickEvent(user, querySuggestion, position) {
  return {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedQuerySuggestion,
    experiments: formatExperimentsForEventAttributes(user, [experiments.MARKETPLACE_QUERY_SUGGESTIONS_V2]),
    query: querySuggestion.query,
    make: querySuggestion.make,
    category: querySuggestion.category,
    productType: querySuggestion.productType,
    position,
  };
}

function renderPlaceholder(displayPills) {
  const classes = classNames(
    {
      'query-suggestion-pill': displayPills,
      'query-suggestion-pill__placeholder': displayPills,
      'query-suggestion-tile': !displayPills,
      'query-suggestion-tile__placeholder': !displayPills,
    },
  );

  if (displayPills) {
    return (
      <div className={classes}>
        <div className="query-suggestion-pill__text" />
      </div>
    );
  }

  return (
    <div className={classes}>
      <div className="query-suggestion-tile__image-container" >
        <div className="query-suggestion-tile__image"/>
      </div>
    </div>
  );
}

function QuerySuggestion({ suggestion, idx, displayPills }) {
  const user = useUser();
  const urlParams = {
    query: suggestion.query,
    product_type: suggestion.productType,
    make: suggestion.make,
    category: suggestion.category,
  };

  Object.keys(urlParams).forEach(k => !urlParams[k] && delete urlParams[k]);
  const path = Paths.marketplace.expand(urlParams);

  if (displayPills) {
    return (
      <CoreLink
        className="query-suggestion-pill"
        key={`marketplace-query-suggestion-pill-${idx}`}
        clickEvent={buildClickEvent(user, suggestion, idx)}
        to={path}
      >
        <div className="query-suggestion-pill__text">
          {suggestion?.query}
        </div>
        <div className="query-suggestion-pill__icon">
          <RCIcon svgComponent={SearchIcon} inline/>
        </div>
      </CoreLink>
    );
  }

  return (
    <CoreLink
      className="query-suggestion-tile"
      key={`marketplace-query-suggestion-tile-${idx}`}
      clickEvent={buildClickEvent(user, suggestion, idx)}
      to={path}
    >
      <div
        className="query-suggestion-tile__image-container"
        key={`query-suggestion-tile-photo-${idx}`} >
        <div
          className="query-suggestion-tile__image"
          style={backgroundImageStyle(suggestion)}
        />
      </div>
      <div className="query-suggestion-tile__text">
        {suggestion?.query}
      </div>
      <div className="query-suggestion-tile__icon">
        <RCIcon svgComponent={SearchIcon} inline/>
      </div>
    </CoreLink>
  );
}

export function MarketplaceQuerySuggestions(props: IProps) {
  const { querySuggestions, loading } = props;
  const user = useUser();

  if ((!loading && !querySuggestions?.length)) { return null; }

  const expGroup = experimentInt(user, experiments.MARKETPLACE_QUERY_SUGGESTIONS_V2);
  const shouldDisplayPills = expGroup === 2;
  const classes = classNames(
    'query-suggestions-row',
    {
      'query-suggestions-pills': shouldDisplayPills,
      'query-suggestions-tiles': !shouldDisplayPills,
    },
  );

  return (
    <div className={classes}>
      <OverflowingRow
        title={I18n.t('discovery.marketplace.querySuggestions.relatedSearches')}
        collectionCount={querySuggestions.length}
      >
        <Tiles
          loading={loading}
          placeholder={renderPlaceholder(shouldDisplayPills)}
          singleRow
          oneWideMobile
        >
          { !!querySuggestions.length && querySuggestions.map((qs, idx) => {
            return (
              <QuerySuggestion
                suggestion={qs}
                idx={idx}
                displayPills={shouldDisplayPills}
                key={idx} />
            );
          })}
        </Tiles>
      </OverflowingRow>
    </div>
  );
}

export default withQuerySuggestions<IProps>(MarketplaceQuerySuggestions);
